/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { useEffect, useState } from 'react'

import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { registerLicense } from '@syncfusion/ej2-base'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import brandPrimaryLogo from 'assets/images/mockingbird-logo-primary.png'
import brandPrimaryText from 'assets/images/mockingbird-primary-text.png'
import theme from 'assets/theme'
import themeDark from 'assets/theme-dark'
import { setMiniSidenav, useMaterialUIController } from 'context'
import Sidenav from 'modules/Sidenav'
import { AuthRoute, ProtectedRoute, ROUTE_PATHS, RouteType } from 'routes'
import './App.css'

// Registering Syncfusion license key
registerLicense(process.env.REACT_APP_SYNCFUSION_EJ_LICENSE_KEY)

const App = () => {
	const [controller, dispatch] = useMaterialUIController()
	const { miniSidenav, layout, darkMode, session, routes } = controller
	const [onMouseEnter, setOnMouseEnter] = useState(false)
	const { pathname } = useLocation()

	// Open sidenav when mouse enter on mini sidenav
	const handleOnMouseEnter = () => {
		if (miniSidenav && !onMouseEnter) {
			setMiniSidenav(dispatch, false)
			setOnMouseEnter(true)
		}
	}

	// Close sidenav when mouse leave mini sidenav
	const handleOnMouseLeave = () => {
		if (onMouseEnter) {
			setMiniSidenav(dispatch, true)
			setOnMouseEnter(false)
		}
	}

	// Setting page scroll to 0 when changing the route
	useEffect(() => {
		document.documentElement.scrollTop = 0
		document.scrollingElement.scrollTop = 0
	}, [pathname])

	const getRoutes = (allRoutes: RouteType[], routeType: 'auth' | 'protected' | 'other'): any =>
		allRoutes.map(route => {
			if (route.collapse) {
				return getRoutes(route.collapse, routeType)
			}

			//Only render the route if we specified the auth routes and the screens where auth is not required
			if (route.route && routeType === 'auth' && route.authRequired === false) {
				return <Route path={route.route} element={route.component} key={route.key} />
			}

			// Only render the route if we specified the protected routes and auth is required
			if (route.route && routeType === 'protected' && route.authRequired === true) {
				return <Route path={route.route} element={route.component} key={route.key} />
			}

			if (route.route && routeType === 'other' && route.authRequired === undefined) {
				return <Route path={route.route} element={route.component} key={route.key} />
			}

			return null
		})

	const INIT_ROUTE = ROUTE_PATHS.AUTHENTICATION.SIGN_IN

	return (
		<ThemeProvider theme={darkMode ? themeDark : theme}>
			<CssBaseline />
			{layout === 'dashboard' && session && (
				<>
					<Sidenav
						userEmail={session.user.email.slice(0, 15) + '...'}
						color={'primary'}
						brand={brandPrimaryLogo}
						brandName={brandPrimaryText}
						routes={routes}
						onMouseEnter={handleOnMouseEnter}
						onMouseLeave={handleOnMouseLeave}
					/>
				</>
			)}
			<Routes>
				<Route element={<AuthRoute user={!!session} />}>{getRoutes(routes, 'auth')}</Route>
				<Route element={<ProtectedRoute user={!!session} />}>
					{getRoutes(routes, 'protected')}
				</Route>
				{getRoutes(routes, 'other')}
				{/* TODO: This is where it is going to go when it can't find the route but we want 404 */}
				<Route path="*" element={<Navigate to={INIT_ROUTE} replace />} />
			</Routes>
		</ThemeProvider>
	)
}

export default App
